import { motion, type Variants } from "framer-motion";
import { ExternalLink } from "lucide-react";

const fadeUpVariant = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
} satisfies Variants;

type Qualifikation = {
  title: string;
  subtitle: string;
  desc: string;
  pdf: string;
  openable?: boolean;
};

const qualifikationen: Qualifikation[] = [
  {
    title: "Meisterbrief Carina Britz",
    subtitle: "Meisterbrief",
    desc: "Metallbauer-Handwerk · Handwerkskammer Region Stuttgart",
    pdf: "/zertifikate/meisterbrief-carina.pdf",
  },
  {
    title: "Meisterbrief Felix Britz",
    subtitle: "Meisterbrief",
    desc: "Metallbauer-Handwerk · Handwerkskammer Region Stuttgart",
    pdf: "/zertifikate/meisterbrief-felix.pdf",
  },
  {
    title: "Schweißfachmann",
    subtitle: "International Welding Specialist",
    desc: "Nachweis über fachliche Qualifikation im Bereich Schweißtechnik vom Internationalen Institut für Schweißen (IIW).",
    pdf: "/zertifikate/schweissfachmann.pdf",
  },
  {
    title: "ISO 9001:2015 Zertifizierung",
    subtitle: "Qualitätsmanagementsystem",
    desc: "Zertifiziert für Qualität im Bereich Verkauf, Wartung und Reparatur von Brandschutztüren, Garagen- und Industrietoren. Ausgestellt von DEKRA Certification GmbH, Stuttgart.",
    pdf: "/zertifikate/zertifikat-iso-9001-2015.pdf",
  },
];

export default function ZertifikatePage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-zinc-50 py-16 md:py-24 border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <motion.p
            className="text-primary font-bold tracking-wider uppercase text-sm mb-3"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Zertifikate & Nachweise
          </motion.p>
          <motion.h1
            className="text-3xl md:text-5xl font-bold text-zinc-900 mb-5 leading-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            Unsere Zertifizierungen
          </motion.h1>
          <motion.p
            className="text-zinc-600 text-lg leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.2 }}
          >
            Unsere Arbeit erfüllt höchste Qualitätsstandards. Hier finden Sie unsere aktuellen Zertifikate und Nachweise.
          </motion.p>
        </div>
      </section>

      {/* Qualifikationen – gemeinsame Section */}
      <section className="py-14 md:py-20 bg-white border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-primary font-bold tracking-wider uppercase text-sm mb-2">Qualifikation & Zertifizierung</p>
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-3">Qualifikationen & Nachweise</h2>
            <p className="text-zinc-500 text-sm max-w-xl mx-auto">
              Fachliche Qualifikationen, Meisterkompetenz und zertifizierte Qualitätsstandards für zuverlässige und fachgerechte Arbeit im Bereich Türen, Tore und Metallbau.
            </p>
            <p className="mt-3 text-zinc-500 text-sm max-w-xl mx-auto">
              Sollten Sie weitere Nachweise wie z.B. Schulungszertifikate, Stapler- oder Hebebühnenscheine, etc. für Arbeiten in Ihrem Betrieb wünschen, kontaktieren Sie uns gerne.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {qualifikationen.map((item, i) => {
              const isOpenable = item.openable !== false;

              return (
              <motion.a
                key={item.title}
                href={isOpenable ? item.pdf : undefined}
                target={isOpenable ? "_blank" : undefined}
                rel={isOpenable ? "noopener noreferrer" : undefined}
                aria-disabled={!isOpenable}
                tabIndex={isOpenable ? undefined : -1}
                className={`group block rounded-2xl border border-zinc-200 bg-white transition-all duration-300 overflow-hidden ${
                  isOpenable ? "hover:border-primary/30 hover:shadow-lg" : "cursor-default"
                }`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
              >
                {/* PDF-Vorschau */}
                <div className="relative w-full h-56 bg-zinc-50 overflow-hidden border-b border-zinc-100">
                  <iframe
                    src={`${item.pdf}#toolbar=0&navpanes=0&scrollbar=0&page=1`}
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ border: "none", width: "200%", height: "200%", transform: "scale(0.5)", transformOrigin: "0 0" }}
                    title={item.title}
                  />
                  <div className="absolute inset-0 bg-transparent group-hover:bg-black/5 transition-colors duration-300 flex items-end justify-end p-2.5">
                    {isOpenable && (
                      <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold px-2.5 py-1 rounded-full shadow flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" /> Ansehen
                      </span>
                    )}
                  </div>
                </div>
                {/* Info */}
                <div className="px-5 py-4">
                  <p className="text-xs font-bold tracking-wider uppercase text-primary mb-0.5">{item.subtitle}</p>
                  <p className="text-sm font-bold text-zinc-900 mb-1">{item.title}</p>
                  <p className="text-xs text-zinc-500 leading-relaxed">{item.desc}</p>
                </div>
              </motion.a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info-Block */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            className="text-center bg-zinc-50 rounded-2xl p-10 md:p-14 border border-zinc-100"
            initial={fadeUpVariant.hidden}
            whileInView={fadeUpVariant.visible}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-4">
              Qualität und Fachkompetenz
            </h2>
            <p className="text-zinc-600 mb-8 text-lg max-w-xl mx-auto">
              Mit fachlicher Qualifikation, Meisterkompetenz und zertifizierten Qualitätsstandards stehen wir für zuverlässige und fachgerechte Arbeit im Bereich Türen, Tore und Metallbau.
            </p>
            <a
              href="tel:0794133514"
              className="inline-flex items-center justify-center gap-2 bg-primary text-white font-semibold px-8 py-3 rounded-full hover:bg-primary/90 transition-colors"
            >
              07941 33 51 4
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
